import React, { useState, useEffect, useRef } from 'react';
import { Download, Apple, Monitor, Terminal, Gift } from 'lucide-react';
import { fireEvent } from '../utils/funnelEvents';
import MobileDownloadCTA, { useIsPhone, generateEventId, readCookie } from '../components/MobileDownloadCTA';

function readFbclidFromAttribution() {
  const raw = readCookie('sortio_attribution');
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    return parsed.fbclid || null;
  } catch {
    return null;
  }
}

// Did this visitor arrive through a referral link? ReferralPage (/r/<code>)
// writes referral_code into the sortio_attribution cookie and bridges to
// /download?ref=1, so either signal counts. The reward itself settles
// server-side (api/src/services/referral.ts: Stripe coupon
// referral_first_month, amount_off 1499, applied to the referred user's
// first invoice at in-app checkout).
function readReferralSignal() {
  if (typeof window === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).get('ref') === '1') return true;
  } catch {
    // ignore malformed query strings
  }
  const raw = readCookie('sortio_attribution');
  if (!raw) return false;
  try {
    return Boolean(JSON.parse(decodeURIComponent(raw)).referral_code);
  } catch {
    return false;
  }
}

// Detect the visitor's desktop OS. Returns 'macos' | 'windows' | 'linux' or
// null when unsure (bots, mobile UAs, exotic platforms), in which case the
// page keeps the neutral three-card layout that the prerender bakes in.
function detectPlatform() {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  // Mobile visitors get the email-capture CTA via the matchMedia branch;
  // never let an Android UA (which contains "Linux") promote the Linux card.
  if (/Android|iPhone|iPad|iPod/i.test(ua)) return null;
  const probe = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || ua;
  if (/Mac/i.test(probe)) return 'macos';
  if (/Win/i.test(probe)) return 'windows';
  if (/Linux|X11/i.test(probe)) return 'linux';
  return null;
}

const trackDownloadClick = (platform, format) => {
  const eventId = generateEventId();

  if (window.gtag) {
    // Existing GA4 custom event for our own analytics
    window.gtag('event', 'download_click', {
      platform,
      format,
    });

    // GA4 standard event 'generate_lead'. Auto-promoted to a key event
    // by GA4, then importable as a Google Ads conversion. Replaces the
    // older gtag('event','conversion',{send_to: 'AW-...'}) pattern,
    // which 2024+ Google Ads UI no longer creates labels for.
    // Mark as key event in GA4 dashboard (analytics.google.com,
    // Admin, Events, Mark as key event).
    window.gtag('event', 'generate_lead', {
      value: 1.0,
      currency: 'USD',
      transaction_id: eventId,
      platform,
      format,
    });
  }

  // Meta Pixel "Lead" event (browser-side). Dedupes against server-side
  // mirror via eventID. Used by Meta's algorithm to optimize ad delivery
  // toward users likely to download (far higher volume than Purchase
  // events), so the algorithm has enough training signal even at our
  // current scale.
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq(
      'track',
      'Lead',
      { content_name: 'sortio_download', platform, format },
      { eventID: eventId },
    );
  }

  // Server-side CAPI mirror. Carries cookies + IP + UA so iOS 14.5+ ATT
  // users (whose browser pixel is blocked) still get attributed.
  fetch('https://app.getsortio.com/api/attribution/event', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event_name: 'Lead',
      event_id: eventId,
      event_time: Math.floor(Date.now() / 1000),
      fbp: readCookie('_fbp'),
      fbc: readCookie('_fbc'),
      fbclid: readFbclidFromAttribution(),
    }),
    credentials: 'omit',
    keepalive: true,
  }).catch(() => {});
};

// Download links point at our own /dl/:target redirect, NOT straight at a
// GitHub asset. Two reasons, both of which bit us:
//
//   1. Measurement. Every analytics script on this site is consent-gated, so
//      download clicks were only ever counted for visitors who accepted the
//      cookie banner. The redirect is a server-side count that needs no JS and
//      cannot be blocked or declined, because it IS the navigation the visitor
//      asked for.
//   2. Staleness. This file used to hardcode the version in five places, so
//      every release meant hand-editing it and the download button served the
//      previous build until someone remembered. /dl/:target resolves the asset
//      from the live GitHub release at request time, so there is nothing here
//      left to forget.
//
// `/dl/*` is a Vercel rewrite straight to app.getsortio.com (see vercel.json),
// deliberately with no function in between: the money CTA gets no cold start
// and no extra failure mode. Vercel does not forward its geo headers on an
// external rewrite, so these rows carry no country of their own — country comes
// from joining the visitor's /download pageview on (visitor_hash, day), which
// is the same visitor by construction.
//
// Valid targets are defined server-side in api/src/routes/landing-telemetry.
// There is NO rpm and NO 32-bit Windows asset, so the page must not offer either.
const DL = (target) => `/dl/${target}`;

const PLATFORM_CARDS = [
  {
    id: 'macos',
    name: 'macOS',
    Icon: Apple,
    orbClass: 'bg-sortio-primary/10',
    iconHoverClass: 'group-hover:bg-sortio-primary/20',
    href: DL('macos'),
    format: 'dmg',
    buttonLabel: 'Download for macOS',
    description: 'Universal build: runs natively on Apple Silicon and Intel Macs.',
    note: 'macOS 11.0 or later. Open the .dmg, then drag Sortio to your Applications folder.',
  },
  {
    id: 'windows',
    name: 'Windows',
    Icon: Monitor,
    orbClass: 'bg-sortio-secondary/10',
    iconHoverClass: 'group-hover:bg-sortio-secondary/20',
    href: DL('windows'),
    format: 'exe',
    buttonLabel: 'Download for Windows',
    description: 'For Windows 10 and 11. One 64-bit installer, nothing to configure.',
    note: 'Windows 10 or later (64-bit)',
  },
  {
    id: 'linux',
    name: 'Linux',
    Icon: Terminal,
    orbClass: 'bg-sortio-primary/10',
    iconHoverClass: 'group-hover:bg-sortio-primary/20',
    href: DL('linux-appimage'),
    format: 'appimage',
    buttonLabel: 'Download AppImage',
    description: 'AppImage runs on most distributions. DEB package available for Ubuntu and Debian.',
    note: 'Ubuntu, Debian, Fedora, and more',
    secondary: {
      href: DL('linux-deb'),
      format: 'deb',
      label: 'Download DEB',
    },
  },
];

const PlatformCard = ({ card, highlighted }) => {
  const { Icon } = card;
  return (
    <div className={`apple-panel group p-8 relative overflow-hidden${highlighted ? ' ring-2 ring-sortio-primary' : ''}`}>
      <div className={`absolute -bottom-16 -right-16 w-32 h-32 ${card.orbClass} rounded-full blur-2xl opacity-70 group-hover:opacity-100 transition-opacity duration-700`}></div>

      {/* Reserved-height row so the badge never overlaps the title and all
          three cards stay vertically aligned whether or not they're detected. */}
      <div className="h-7 mb-4">
        {highlighted && (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-sortio-primary/15 border border-sortio-primary/40 text-xs font-medium text-sortio-primary">
            Detected: {card.name}
          </span>
        )}
      </div>

      <a
        href={card.href}
        onClick={() => trackDownloadClick(card.id, card.format)}
        className="flex items-center mb-6 cursor-pointer hover:opacity-80 transition-opacity"
      >
        <div className={`p-3 bg-apple-800/50 rounded-2xl mr-4 ${card.iconHoverClass} transition-colors`}>
          <Icon size={32} className="text-apple-50" />
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-apple-50">{card.name}</h2>
          <span className="text-xs text-apple-400">Click to download</span>
        </div>
      </a>

      <p className="text-apple-300 mb-6">{card.description}</p>

      <div className="space-y-4">
        <a
          href={card.href}
          onClick={() => trackDownloadClick(card.id, card.format)}
          className="flex items-center justify-center gap-2 w-full py-3.5 px-6 rounded-xl font-semibold text-base text-white bg-sortio-primary hover:bg-sortio-primary/90 transition-all duration-200 shadow-md hover:shadow-lg"
        >
          <Download className="w-5 h-5" />
          <span>{card.buttonLabel}</span>
        </a>

        {card.secondary && (
          <a
            href={card.secondary.href}
            onClick={() => trackDownloadClick(card.id, card.secondary.format)}
            className="apple-panel-button w-full py-3 flex justify-center"
          >
            <Download className="w-5 h-5 mr-2" />
            <span>{card.secondary.label}</span>
          </a>
        )}

        <div className="text-center text-apple-400 text-sm">{card.note}</div>
      </div>
    </div>
  );
};

// "What happens after you install": sets expectations for the account
// requirement (magic-link sign-in is a known deliverability failure mode
// behind corporate mail filters), the free allocation, the safety story,
// and where the Pro purchase actually happens (inside the app).
const POST_INSTALL_STEPS = [
  {
    title: 'Install and open Sortio',
    body: 'The guides below cover macOS, Windows, and Linux.',
  },
  {
    title: 'Sign in with your email',
    body: 'We email you a one-time sign-in link, no password to remember. If it does not arrive within a minute or two, check spam or your company\'s mail filter.',
  },
  {
    title: 'Sort with your free AI trial',
    body: 'No credit card required. A one-time AI trial allowance, enough to try Sortio on a real folder. Rule-based sorting is free and unlimited, and the in-app assistant is always available.',
  },
  {
    title: 'Preview first, undo anytime',
    body: 'Every sort shows the full plan before a single file moves, and you can undo a sort after it runs.',
  },
  {
    title: 'Upgrade inside the app when you need more',
    body: 'Pro is $14.99/mo or $99/yr. Click upgrade in the app and secure checkout opens in your browser.',
  },
];

const DownloadPage = () => {
  // False during the prerender and the client's first render (no hydration
  // mismatch, see T0-1); flips to the mobile CTA on a phone after mount.
  const isMobile = useIsPhone();
  // Detected OS, null during prerender AND the client's first render (same
  // T0-1 guard): the static HTML keeps the neutral three-card layout and the
  // visitor's platform is promoted post-mount only.
  const [platform, setPlatform] = useState(null);
  // Referred visitors (via /r/<code>) get a banner confirming the
  // first-month Pro discount, which otherwise stays invisible until
  // in-app checkout.
  const [invited, setInvited] = useState(false);
  // Escape hatch: a visitor who lands on the mobile CTA anyway (detection is
  // never perfect) can ask for the desktop cards and we stop second-guessing.
  const [forceDesktop, setForceDesktop] = useState(false);
  const ctaLogged = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Same ReactSnap guard as useIsPhone: the prerendered HTML must stay on
    // the neutral, non-personalized branch.
    if (navigator.userAgent === 'ReactSnap') return;
    setPlatform(detectPlatform());
    if (readReferralSignal()) {
      setInvited(true);
      fireEvent('referral_banner_shown', { page: '/download' });
    }
  }, []);

  const showMobileCTA = isMobile && !forceDesktop;

  // A silent branch is an unmeasurable one: the desktop misfire above was only
  // visible by cross-referencing server-side user agents against the email
  // captures weeks later. Log every render of the CTA with what we detected,
  // so a future misfire shows up as `detected_platform != unknown` here.
  useEffect(() => {
    if (!showMobileCTA || ctaLogged.current) return;
    ctaLogged.current = true;
    fireEvent('download_mobile_cta_shown', {
      detected_platform: platform || 'unknown',
      viewport_width: typeof window === 'undefined' ? 0 : window.innerWidth,
      touch_points: typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints || 0,
    });
  }, [showMobileCTA, platform]);

  const showDesktopAnyway = () => {
    fireEvent('download_mobile_cta_override', {
      detected_platform: platform || 'unknown',
      viewport_width: typeof window === 'undefined' ? 0 : window.innerWidth,
    });
    setForceDesktop(true);
  };

  const orderedCards = platform
    ? [
        ...PLATFORM_CARDS.filter((c) => c.id === platform),
        ...PLATFORM_CARDS.filter((c) => c.id !== platform),
      ]
    : PLATFORM_CARDS;

  return (
    <div className="min-h-screen bg-black pt-24 pb-16 text-white">
      {/* Background blur orbs */}
      <div className="fixed -bottom-32 -right-32 w-96 h-96 bg-sortio-primary/10 rounded-full blur-3xl"></div>
      <div className="fixed -top-32 -left-32 w-96 h-96 bg-sortio-secondary/10 rounded-full blur-3xl"></div>

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        {/* Hero section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-apple-50 to-sortio-primary">Download Sortio</span>
          </h1>
          <p className="text-apple-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Free download. Free AI trial included, no credit card required.
          </p>
        </div>

        {/* Referral banner: only rendered for visitors who arrived via /r/<code> */}
        {invited && (
          <div className="apple-panel max-w-3xl mx-auto mb-12 px-6 py-4 flex items-center gap-4 border border-sortio-primary/40">
            <div className="p-2.5 bg-sortio-primary/15 rounded-xl shrink-0">
              <Gift className="w-5 h-5 text-sortio-primary" />
            </div>
            <p className="text-apple-200 text-sm md:text-base text-left">
              <span className="font-semibold text-apple-50">You were invited.</span>{' '}
              Your first month of Pro is discounted automatically: download the app, sign in,
              and the discount applies at checkout when you upgrade.
            </p>
          </div>
        )}

        {/* Handheld + narrow: email-capture CTA, since Sortio is desktop-only.
            Everyone else, including anyone who takes the escape hatch below,
            gets the platform cards. */}
        {showMobileCTA ? (
        <div>
          <MobileDownloadCTA />
          <div className="text-center mt-5">
            <button
              type="button"
              onClick={showDesktopAnyway}
              className="text-sm text-apple-400 hover:text-apple-200 underline underline-offset-4"
            >
              Not on a phone? Show the desktop downloads
            </button>
          </div>
        </div>
        ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {orderedCards.map((card) => (
            <PlatformCard key={card.id} card={card} highlighted={card.id === platform} />
          ))}
        </div>
        )}

        {/* What happens after install */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-3xl font-semibold mb-8 text-center">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-apple-100 to-apple-50">What happens after you install</span>
          </h2>
          <div className="apple-panel p-8">
            <ol className="space-y-5">
              {POST_INSTALL_STEPS.map((step, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-sortio-primary/15 border border-sortio-primary/40 text-sortio-primary text-sm font-semibold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <p className="text-apple-300 leading-relaxed">
                    <span className="font-semibold text-apple-50">{step.title}.</span>{' '}
                    {step.body}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Installation Guide */}
        <div className="mt-20">
          <h2 className="text-3xl font-semibold mb-8 text-center">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-apple-100 to-apple-50">Installation Guide</span>
          </h2>

          <div className="apple-panel p-8">
            <div className="space-y-6">
              <div className="border-l-4 border-sortio-primary pl-4 py-2">
                <h3 className="text-xl font-semibold text-apple-50 mb-2">macOS Installation</h3>
                <ol className="list-decimal list-inside space-y-2 text-apple-300">
                  <li>Download the .dmg using the macOS button above.</li>
                  <li>Open the .dmg and drag Sortio into your Applications folder.</li>
                  <li>Launch Sortio from Applications. If macOS asks to confirm opening an app downloaded from the internet, click Open. Sortio is notarized by Apple.</li>
                  <li>Sign in with your email and follow the onboarding steps.</li>
                </ol>
              </div>

              <div className="border-l-4 border-sortio-secondary pl-4 py-2">
                <h3 className="text-xl font-semibold text-apple-50 mb-2">Windows Installation</h3>
                <ol className="list-decimal list-inside space-y-2 text-apple-300">
                  <li>Download the 64-bit installer using the Windows button above.</li>
                  <li>Run the installer and follow the setup wizard. The installer is code signed, so Windows should not warn you. If SmartScreen does appear because the new signature has not built reputation yet, click More info, then Run anyway.</li>
                  <li>Launch Sortio from the Start menu or desktop shortcut.</li>
                  <li>Sign in with your email and follow the onboarding steps.</li>
                </ol>
              </div>

              <div className="border-l-4 border-sortio-primary pl-4 py-2">
                <h3 className="text-xl font-semibold text-apple-50 mb-2">Linux Installation</h3>
                <div className="space-y-3 text-apple-300">
                  <div>
                    <p className="font-semibold">DEB (Ubuntu, Debian):</p>
                    <code className="text-sm bg-apple-900/30 px-2 py-1 rounded">sudo dpkg -i Sortio_*_amd64.deb</code>
                  </div>
                  <div>
                    <p className="font-semibold">AppImage (Fedora, RHEL, Arch, and other distributions):</p>
                    <ol className="list-decimal list-inside space-y-1 ml-4">
                      <li>Make the file executable: <code className="text-sm bg-apple-900/30 px-2 py-1 rounded">chmod +x Sortio_*_amd64.AppImage</code></li>
                      <li>Run the AppImage: <code className="text-sm bg-apple-900/30 px-2 py-1 rounded">./Sortio_*_amd64.AppImage</code></li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
